import React, { useState } from 'react'
import {
  Flex,
  Text,
  Button,
  Box,
  Heading,
  Avatar,
  Card,
  CardHeader,
  CardBody,
  Stack,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import * as Sentry from '@sentry/browser'
import { useSendPrequalStep } from '../../../../../hooks/homebotNetwork/verificationFlow/useSendPrequalStep'

const MSG = defineMessages({
  cancel: {
    id: 'SendPrequalView.cancel',
    defaultMessage: 'Cancel'
  },
  messageInTouch: {
    id: 'SendPrequalView.messageInTouch',
    defaultMessage: 'Your real estate agent will be in touch soon to answer your question.'
  },
  prequalError: {
    id: 'SendPrequalView.prequalError',
    defaultMessage: 'Failed to send prequal request'
  },
  prequalExplanation: {
    id: 'SendPrequalView.prequalExplanation',
    defaultMessage: "We'll send your request to {name} and they'll get back to you soon."
  },
  prequalQuestion: {
    id: 'SendPrequalView.prequalQuestion',
    defaultMessage: 'In the meantime, would you like to get pre-qualified?'
  },
  prequalSuccess: {
    id: 'SendPrequalView.prequalSuccess',
    defaultMessage: 'Pre-qualification request sent!'
  },
  requestPrequal: {
    id: 'SendPrequalView.requestPrequal',
    defaultMessage: 'Request a pre-qual'
  },
  title: {
    id: 'SendPrequalView.title',
    defaultMessage: '✅ Message sent!'
  }
})

/**
 * SendPrequalView Component
 *
 * Confirmation-style modal for sending a pre-qualification request to verified loan officers.
 */
const SendPrequalView = () => {
  const intl = useIntl()
  const toast = useToast()
  const { onSubmit, isLoading, onCancel, customerFirstName, customerLastName, customerPhotoUri } = useSendPrequalStep()

  const handleSubmit = async () => {
    try {
      await onSubmit()
      toast({
        title: intl.formatMessage(MSG.prequalSuccess),
        status: 'success'
      })
    } catch (error) {
      Sentry.captureException(error)
      toast({
        title: intl.formatMessage(MSG.prequalError),
        status: 'error'
      })
    }
  }

  const loanOfficerName = `${customerFirstName} ${customerLastName}`

  return (
    <Flex direction='column' justifyContent='space-between' data-qa='schedule-tour-send-prequal-view'>
      <Box>
        <Text size='xs' mb={4}>
          {intl.formatMessage(MSG.messageInTouch)}
        </Text>

        <Card mb={6}>
          <CardHeader pb={0} backgroundColor='card-bg-layer-3'>
            <Flex alignItems='center' gap={3}>
              <Avatar src={customerPhotoUri} name={loanOfficerName} size='sm' />
              <Heading textAlign='left' mb={0} size='sm'>
                {intl.formatMessage(MSG.prequalQuestion)}
              </Heading>
            </Flex>
          </CardHeader>
          <CardBody backgroundColor='card-bg-layer-3'>
            <Text size='xs'>
              {intl.formatMessage(MSG.prequalExplanation, {
                name: customerFirstName
              })}
            </Text>
          </CardBody>
        </Card>
      </Box>
      {/* Footer buttons */}
      <Stack direction='row' gap={3} justifyContent='flex-end'>
        <Button
          onClick={onCancel}
          size='sm'
          isDisabled={isLoading}
          tracking={{
            guid: 'k3VL8qSRNJcBuvmgY9gLRS',
            ui_context: 'verification_flow_send_prequal_view',
            descriptor: MSG.cancel.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.cancel)}
        </Button>
        <Button
          onClick={handleSubmit}
          colorScheme='primary'
          size='sm'
          isLoading={isLoading}
          tracking={{
            guid: 'mbyC8naAZx9FH8DpsPxLX3',
            ui_context: 'verification_flow_send_prequal_view',
            descriptor: MSG.requestPrequal.defaultMessage
          }}
        >
          {intl.formatMessage(MSG.requestPrequal)}
        </Button>
      </Stack>
    </Flex>
  )
}

export default SendPrequalView
